import { useEffect, useRef } from 'react';

const PARTICLE_COUNT = 90;
const MAX_DIST = 150;

const THEMES = {
    dark:  { bg: '#060f08', color: '63, 170, 52' },
    light: { bg: '#e8f5ec', color: '5, 110, 60' },
};

function ConstellationBg({ theme }) {
    const canvasRef = useRef(null);
    const mouse = useRef({ x: -9999, y: -9999 });
    const animRef = useRef(null);
    const themeRef = useRef(theme);

    useEffect(() => {
        themeRef.current = theme;
    }, [theme]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const particles = [];
        let w, h;

        const resize = () => {
            w = canvas.width = window.innerWidth;
            h = canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        for (let i = 0; i < PARTICLE_COUNT; i++) {
            particles.push({
                x: Math.random() * window.innerWidth,
                y: Math.random() * window.innerHeight,
                vx: (Math.random() - 0.5) * 0.6,
                vy: (Math.random() - 0.5) * 0.6,
                r: Math.random() * 1.5 + 1,
                o: Math.random() * 0.35 + 0.55,
            });
        }

        const onMouseMove = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
        window.addEventListener('mousemove', onMouseMove);

        const draw = () => {
            const { bg, color } = THEMES[themeRef.current] || THEMES.dark;

            ctx.fillStyle = bg;
            ctx.fillRect(0, 0, w, h);

            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];

                const dx = mouse.current.x - p.x;
                const dy = mouse.current.y - p.y;
                const md = Math.sqrt(dx * dx + dy * dy);
                if (md < 200) {
                    p.vx += (dx / md) * 0.025;
                    p.vy += (dy / md) * 0.025;
                    const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
                    if (spd > 2) { p.vx = (p.vx / spd) * 2; p.vy = (p.vy / spd) * 2; }
                } else {
                    p.vx *= 0.995;
                    p.vy *= 0.995;
                }

                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0) { p.x = 0; p.vx *= -1; }
                if (p.x > w) { p.x = w; p.vx *= -1; }
                if (p.y < 0) { p.y = 0; p.vy *= -1; }
                if (p.y > h) { p.y = h; p.vy *= -1; }

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${color}, ${p.o})`;
                ctx.fill();

                for (let j = i + 1; j < particles.length; j++) {
                    const q = particles[j];
                    const lx = p.x - q.x;
                    const ly = p.y - q.y;
                    const d = Math.sqrt(lx * lx + ly * ly);
                    if (d < MAX_DIST) {
                        ctx.beginPath();
                        ctx.moveTo(p.x, p.y);
                        ctx.lineTo(q.x, q.y);
                        ctx.strokeStyle = `rgba(${color}, ${(1 - d / MAX_DIST) * 0.45})`;
                        ctx.lineWidth = 1;
                        ctx.stroke();
                    }
                }
            }

            animRef.current = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            cancelAnimationFrame(animRef.current);
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{ position: 'absolute', inset: 0, display: 'block', zIndex: 0 }}
        />
    );
}

export default ConstellationBg;
